import React, { useState } from 'react'

import { twMerge } from 'tailwind-merge'

import Icon from '@components/Icon'
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/react'
import { trans, SITE } from '@helpers/translator'

export interface Profile {
  name: string
  url?: string
  selected?: boolean
}

interface SelectProfileProps {
  profiles: Profile[]
  className?: string
}

/**
 * Composant SelectProfile pour sélectionner un profil et rediriger vers l'URL associée.
 */
const SelectProfile: React.FC<SelectProfileProps> = ({
  profiles = [],
  className = '',
}) => {
  const [currentProfile, setCurrentProfile] = useState<Profile>(() => {
    return profiles.find((profile) => profile.selected) || profiles[0] || {}
  })

  return (
    <div className={twMerge(`relative w-[199px] md:w-[225px] ${className}`)}>
      <Listbox
        value={currentProfile}
        onChange={(profile: Profile) => {
          setCurrentProfile(profile)
          if (profile.url) {
            window.location.href = profile.url
          }
        }}
      >
        <div className="relative">
          <ListboxButton className="flex justify-between items-center w-full bg-brand text-black py-[15.5px] ltr:pl-4 rtl:pr-4 ltr:pr-[21px] rtl:pl-[21px] h-[55px] [&[data-open]_svg]:rotate-180">
            <div className="ltr:text-left rtl:text-right">
              <span className="block text-xs text-black/50">{trans(SITE)}</span>
              <span className="block text-sm font-semibold leading-[15px]">
                {currentProfile.name}
              </span>
            </div>
            <Icon
              name="chevron_down"
              size="7"
              className="flex-shrink-0 transition duration-200"
              aria-hidden="true"
            />
          </ListboxButton>

          <ListboxOptions
            key={`select-profile-options`}
            anchor="bottom start"
            modal={false}
            transition
            className={
              'w-[199px] md:w-[290px] bg-brand-light z-[1000] origin-top absolute transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 max-h-60 text-base focus:outline-none sm:text-sm z-[2000]'
            }
          >
            {profiles
              .filter((profile) => !profile.selected)
              .map((profile, index, array) => (
                <div key={profile.name} className="px-4">
                  <ListboxOption
                    className="flex items-center cursor-pointer py-4 rounded-xl transition-all duration-300 group hover:bg-brand hover:px-3 hover:my-2"
                    key={profile.name}
                    value={profile}
                  >
                    <div className="flex flex-col text-sm font-[450] group-hover:font-semibold">
                      <span className="text-xs text-black/50 font-semibold">
                        {trans(SITE)}
                      </span>
                      {profile.name}
                    </div>
                    <div className="ltr:ml-auto rtl:mr-auto opacity-0 group-hover:opacity-100 transition-all delay-50 duration-300 bg-deep-black rounded-full p-1.5">
                      <Icon
                        className="text-white"
                        name="arrow"
                        size="9"
                        aria-hidden="true"
                      />
                    </div>
                  </ListboxOption>
                  {index < array.length - 1 && <hr />}
                </div>
              ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  )
}

export default SelectProfile
